import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Track if we just logged in to skip re-validation
  const justLoggedIn = useRef(false);

  useEffect(() => {
    // Check for stored auth token and validate
    const initAuth = async () => {
      // Skip validation if we just logged in (prevents race condition)
      if (justLoggedIn.current) {
        justLoggedIn.current = false;
        setLoading(false);
        return;
      }
      
      const token = localStorage.getItem('lebonlux_token');
      const storedUser = localStorage.getItem('lebonlux_user');
      
      if (token && storedUser) {
        try {
          // First set user from localStorage immediately (faster UX)
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          
          // Then validate token in background
          const userData = await authAPI.getMe();
          setUser(userData);
          localStorage.setItem('lebonlux_user', JSON.stringify(userData));
        } catch (error) {
          // Token invalid, clear storage
          console.error('Token validation failed:', error);
          localStorage.removeItem('lebonlux_token');
          localStorage.removeItem('lebonlux_user');
          setUser(null);
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login(email, password);
    const { access_token, user: userData } = response;
    
    localStorage.setItem('lebonlux_token', access_token);
    localStorage.setItem('lebonlux_user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await authAPI.register(name, email, password);
    const { access_token, user: userData } = response;
    
    localStorage.setItem('lebonlux_token', access_token);
    localStorage.setItem('lebonlux_user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('lebonlux_token');
    localStorage.removeItem('lebonlux_user');
  };

  const updateUser = async (updates) => {
    try {
      const updatedUser = await authAPI.updateMe(updates);
      setUser(updatedUser);
      localStorage.setItem('lebonlux_user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await authAPI.getMe();
      setUser(userData);
      localStorage.setItem('lebonlux_user', JSON.stringify(userData));
      return userData;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  };

  // Helper functions for direct state updates (used by AuthCallback)
  const setToken = (token) => {
    localStorage.setItem('lebonlux_token', token);
  };
  
  // Wrapper for setUser that marks we just logged in
  const setUserWithFlag = (userData) => {
    justLoggedIn.current = true;
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser: setUserWithFlag,
      setToken,
      loading, 
      login, 
      register, 
      logout, 
      updateUser,
      refreshUser,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
